/// <reference types="@shopify/polaris-types" />
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAutocomplete } from "../../hooks/useAutocomplete";
import { useShopifySaveBar } from "../../hooks/useShopifySaveBar";
import { PackCard } from "./PackCard";
import { BalanceDisplay } from "./BalanceDisplay";
import { ExplainerSection } from "./ExplainerSection";
import { ToggleSection } from "./ToggleSection";
import { PendingModal } from "./PendingModal";
import { PurchaseHistory } from "./PurchaseHistory";
import styles from "./AddressValidatorPage.module.css";

// ── Router shell ────────────────────────────────────────────────────────────

export default function AddressValidatorPage() {
  const [searchParams] = useSearchParams();
  const hasChargeId = searchParams.has("charge_id");
  const [billingWasPending] = useState(
    () => localStorage.getItem("codway_autocomplete_billing_pending") === "1"
  );
  useEffect(() => {
    if (billingWasPending)
      localStorage.removeItem("codway_autocomplete_billing_pending");
  }, [billingWasPending]);
  return (
    <AddressValidatorPageInner
      showSuccessBanner={hasChargeId}
      showCancelledBanner={billingWasPending && !hasChargeId}
    />
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

function AddressValidatorPageInner({
  showSuccessBanner = false,
  showCancelledBanner = false,
}: {
  showSuccessBanner?: boolean;
  showCancelledBanner?: boolean;
}) {
  const {
    data,
    loading,
    reloading,
    error,
    toggling,
    buying,
    toggle,
    buyPack,
    reload,
  } = useAutocomplete();
  const [pendingModal, setPendingModal] = useState(false);
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);

  useShopifySaveBar({
    isDirty: pendingEnabled !== null,
    isSaving: toggling,
    onSave: async () => {
      try {
        await toggle(pendingEnabled!);
        setPendingEnabled(null);
      } catch {
        // error displayed via state.error; save bar stays open so user can retry
      }
    },
    onDiscard: () => setPendingEnabled(null),
  });

  if (loading) {
    return (
      <s-page heading="Google Autocomplete">
        <s-section>
          <s-spinner accessibility-label="Cargando…" />
        </s-section>
      </s-page>
    );
  }

  if (error) {
    return (
      <s-page heading="Google Autocomplete">
        <s-section>
          <s-banner tone="critical" heading="Error al cargar">
            {error}
          </s-banner>
        </s-section>
      </s-page>
    );
  }

  const credits = data?.credits ?? 0;
  const enabled = data?.enabled ?? false;
  const isDepleted = credits === 0 && enabled;
  const isFreshInstall =
    credits === 0 && (data?.recent_purchases.length ?? 0) === 0;
  const dollarValue = (credits * 0.025).toFixed(2);
  const pendingPackageIds = new Set<string>(
    (data?.recent_purchases ?? [])
      .filter((p) => p.status === "PENDING")
      .map((p) => p.package_id)
  );

  function handleBuy(packageId: string) {
    if (pendingPackageIds.has(packageId)) {
      setPendingModal(true);
      return;
    }
    buyPack(packageId);
  }

  return (
    <s-page heading="Google Autocomplete">
      {showSuccessBanner && (
        <s-banner tone="success" heading="¡Compra procesada!" dismissible>
          Tu pago fue aprobado. Los créditos se acreditarán en unos segundos.
        </s-banner>
      )}
      {showCancelledBanner && (
        <s-banner tone="warning" heading="Compra cancelada" dismissible>
          No se realizó ningún cargo. Puedes intentarlo de nuevo cuando quieras.
        </s-banner>
      )}

      {isDepleted && (
        <s-banner
          tone="warning"
          heading="Sin créditos — el widget está pausado"
        >
          Recarga tu cuenta para que los clientes vuelvan a ver sugerencias de
          dirección.
        </s-banner>
      )}

      <s-grid gridTemplateColumns="3fr 2fr" gap="base">
        <s-section>
          {!isFreshInstall && (
            <BalanceDisplay
              credits={credits}
              dollarValue={dollarValue}
              reloading={reloading}
            />
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "12px",
            }}
          >
            <span
              style={{ fontSize: "14px", fontWeight: 600, color: "#202223" }}
            >
              Compra créditos
            </span>
            <button
              className={styles.reloadButton}
              onClick={reload}
              title="Actualizar créditos"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M10 3V0L6 4l4 4V5a5 5 0 1 1-5 5H3a7 7 0 1 0 7-7z" />
              </svg>
            </button>
          </div>

          <s-grid gridTemplateColumns="1fr 1fr 1fr 1fr" gap="small-300">
            {(data?.packages ?? []).map((pack) => (
              <PackCard
                key={pack.id}
                pack={pack}
                buying={buying}
                onBuy={handleBuy}
              />
            ))}
          </s-grid>

          <div style={{ paddingTop: "12px" }}>
            <s-text color="subdued">
              Cada sesión — definida como el inicio cuando un cliente escribe su
              dirección y el final cuando hace clic en una sugerencia — consume{" "}
              <strong>1 crédito</strong> ($0.025).
            </s-text>
          </div>
        </s-section>

        <ExplainerSection />
      </s-grid>

      <div style={{ paddingTop: "8px" }} />

      <ToggleSection
        enabled={pendingEnabled ?? enabled}
        toggling={toggling}
        credits={credits}
        onToggle={setPendingEnabled}
      />

      {!isFreshInstall && (
        <PurchaseHistory purchases={data?.recent_purchases ?? []} />
      )}

      {pendingModal && <PendingModal onClose={() => setPendingModal(false)} />}
    </s-page>
  );
}
